import React, { useMemo, useState } from 'react'
import TicketRow from './ticketRow'
import {
  Box,
  Chip,
  FormControl,
  Typography,
  Select,
  MenuItem,
  Skeleton,
  InputLabel,
  Checkbox,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { useGetTicketsQuery, useGetTicketByIdQuery, useGetCompaniesQuery } from '../../store/api/adminApi'
import ChatModal from './chatModal'
import PageHeader from '../../shared/components/PageHeader'
import EmptyState from '../../shared/components/EmptyState'
import SharedButton from '../../shared/components/Button'
import { useThemeMode, useTablePagination } from '../../shared/hooks'

export enum SupportStatusEnum {
  OPEN = 1,
  REPLIED = 2,
  CLOSED = 3,
}

export interface Ticket {
  _id: string
  companyId: string
  companyName?: string
  email: string
  requestId: string
  subject: string
  initialMessage: string
  status: number
  createdBy: string
  routePath: string
  issueType: string
  images: string[]
  replies: Reply[]
  createdAt: string
  updatedAt?: string
}

export interface Reply {
  replyBy: string
  message: string
  date: string
  replyById: string
  isSeen: boolean
}

const statusOptions = [
  { label: 'Open', value: 1 },
  { label: 'Replied', value: 2 },
  { label: 'Closed', value: 3 },
]

/** How often the queue re-checks for new tickets while an admin has it open. */
const POLL_INTERVAL_MS = 30_000

/**
 * The company dropdown needs every company, but `/admin/all-companies` is a
 * paginated endpoint that defaults to 10 rows — requesting a high limit is what
 * keeps the filter (and, for legacy rows, the name fallback) complete.
 */
const COMPANY_DROPDOWN_LIMIT = 1000

const Support = () => {
  const { isDark } = useThemeMode()
  const [open, setOpen] = useState(false)
  const [companyFilter, setCompanyFilter] = useState<string>('')
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([])
  const [selectTicket, setSelectTicket] = useState({ companyId: '', ticketId: '' })

  const { page, rowsPerPage, searchValue, handlePageChange, handleRowsPerPageChange, handleSearch } =
    useTablePagination({ key: 'support', initialRowsPerPage: 25 })
  const [searchDraft, setSearchDraft] = useState(searchValue)

  const {
    data: ticketData,
    isLoading: loadingTickets,
    isError: ticketsError,
    refetch: refetchTickets,
  } = useGetTicketsQuery(
    {
      companyId: companyFilter || undefined,
      status: selectedStatuses?.length ? selectedStatuses.join(',') : undefined,
      search: searchValue || undefined,
      skip: page * rowsPerPage,
      limit: rowsPerPage,
    },
    // Without polling an admin sitting on this page never sees a new ticket
    // or a new customer reply until a full manual reload.
    { pollingInterval: POLL_INTERVAL_MS, refetchOnFocus: true, refetchOnReconnect: true }
  )

  const allTickets = ticketData?.tickets ?? []
  const totalCount = ticketData?.total ?? 0
  const statusCounts = ticketData?.statusCounts

  const skipTicketById = selectTicket.companyId === '' || selectTicket.ticketId === ''
  const {
    data: ticketById = null,
    isLoading: loadingTicketById,
    isError: ticketByIdError,
    refetch: refetchTicketById,
  } = useGetTicketByIdQuery(
    { companyId: selectTicket.companyId, ticketId: selectTicket.ticketId },
    { skip: skipTicketById }
  )

  const { data: companiesData } = useGetCompaniesQuery({ deleted: false, limit: COMPANY_DROPDOWN_LIMIT })

  const companyOptions = useMemo(() => {
    return companiesData?.companies?.map((v: any) => ({ label: v.companyName, value: v._id })) ?? []
  }, [companiesData])

  const companyMap = useMemo(() => {
    const map: Record<string, string> = {}
    companiesData?.companies?.forEach((v: any) => {
      map[v._id] = v.companyName
    })
    return map
  }, [companiesData])

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const submitSearch = () => {
    handleSearch(searchDraft.trim())
    handlePageChange(0)
  }

  const resetToFirstPage = () => handlePageChange(0)

  const selectStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '14px',
      bgcolor: 'background.paper',
      '& fieldset': { borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0' },
      '&:hover fieldset': { borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1' },
    },
    '& .MuiInputLabel-root': { fontSize: '14px' },
  }

  const TicketSkeleton = () => (
    <Box
      sx={{
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
        borderRadius: '12px',
      }}
    >
      <Skeleton variant="circular" width={32} height={32} />
      <Box sx={{ flex: 1, display: 'flex', gap: 4 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={80} height={18} />
          <Skeleton variant="text" width={120} height={22} />
        </Box>
        <Box sx={{ flex: 2 }}>
          <Skeleton variant="text" width={80} height={18} />
          <Skeleton variant="text" width={200} height={22} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={80} height={18} />
          <Skeleton variant="text" width={140} height={22} />
        </Box>
        <Box>
          <Skeleton variant="rectangular" width={70} height={26} sx={{ borderRadius: '6px' }} />
        </Box>
      </Box>
    </Box>
  )

  // Counts come from the server over the status-unfiltered query, so selecting
  // "Closed" no longer makes the bar claim there are zero open tickets.
  const statusSummaryItems = [
    {
      label: 'Total',
      count: statusCounts?.total ?? 0,
      color: '#64748b',
      bg: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
    },
    {
      label: 'Open',
      count: statusCounts?.open ?? 0,
      color: '#2563eb',
      bg: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff',
    },
    {
      label: 'Replied',
      count: statusCounts?.replied ?? 0,
      color: '#16a34a',
      bg: isDark ? 'rgba(34,197,94,0.08)' : '#f0fdf4',
    },
    {
      label: 'Closed',
      count: statusCounts?.closed ?? 0,
      color: '#dc2626',
      bg: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2',
    },
  ]

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Support"
        subtitle="Manage and respond to customer support requests"
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              value={searchDraft}
              placeholder="Search ticket, subject or email"
              onChange={(event) => setSearchDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submitSearch()
              }}
              onBlur={submitSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: 0, sm: 240 }, flex: { xs: 1, sm: 'none' }, ...selectStyles }}
            />
            <FormControl
              size="small"
              sx={{ minWidth: { xs: 0, sm: 200 }, flex: { xs: 1, sm: 'none' }, ...selectStyles }}
            >
              <InputLabel id="company-label">Company</InputLabel>
              <Select
                value={companyFilter || 'all'}
                onChange={(event) => {
                  setCompanyFilter(event.target.value === 'all' ? '' : event.target.value)
                  resetToFirstPage()
                }}
                labelId="company-label"
                label="Company"
                MenuProps={{ PaperProps: { style: { maxHeight: 600, overflowY: 'auto' } } }}
              >
                <MenuItem value="all">All</MenuItem>
                {companyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: { xs: 0, sm: 200 }, flex: { xs: 1, sm: 'none' }, ...selectStyles }}
            >
              <InputLabel id="status-label" shrink>
                Status
              </InputLabel>
              <Select
                multiple
                value={selectedStatuses}
                onChange={(event) => {
                  setSelectedStatuses(event.target.value as number[])
                  resetToFirstPage()
                }}
                labelId="status-label"
                label="Status"
                notched
                renderValue={(selected) =>
                  selected.length === 0
                    ? 'All'
                    : statusOptions
                        .filter((option) => selected.includes(option.value))
                        .map((option) => option.label)
                        .join(', ')
                }
                displayEmpty
                MenuProps={{ PaperProps: { style: { maxHeight: 600, overflowY: 'auto' } } }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={selectedStatuses.includes(option.value)} color="primary" />
                    {option.label}
                  </MenuItem>
                ))}
                {selectedStatuses.length > 0 && (
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedStatuses([])
                      resetToFirstPage()
                    }}
                    sx={{ fontSize: '13px', fontWeight: 600, color: 'error.main' }}
                  >
                    Clear Filter
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        }
      />

      {!loadingTickets && !ticketsError && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          {statusSummaryItems.map((item) => (
            <Chip
              key={item.label}
              icon={
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, ml: '8px !important' }} />
              }
              label={`${item.count} ${item.label}`}
              size="small"
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: item.color,
                bgcolor: item.bg,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                borderRadius: '8px',
                px: 0.5,
                '& .MuiChip-icon': { mr: 0.5 },
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {loadingTickets ? (
          [...Array(4)].map((_, i) => <TicketSkeleton key={i} />)
        ) : ticketsError ? (
          // Distinct from the empty state — a failed request must never read as
          // "there are no tickets".
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              Could not load support tickets
            </Typography>
            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mb: 2 }}>
              Something went wrong while fetching the queue.
            </Typography>
            <SharedButton label="Retry" size="small" onClick={() => refetchTickets()} />
          </Box>
        ) : allTickets?.length ? (
          allTickets.map((data: any) => (
            <TicketRow
              key={data._id}
              id={`#${data.requestId || '--'}`}
              subject={data.subject}
              update={data.updatedAt}
              status={data.status}
              replyCount={data.replies?.length || 0}
              hasUnseenReply={data.replies?.some((reply: Reply) => reply.replyBy === 'user' && !reply.isSeen)}
              companyName={data.companyName || companyMap[data?.companyId] || ''}
              handleOpen={handleOpen}
              onClick={() => setSelectTicket({ companyId: data?.companyId, ticketId: data?._id })}
            />
          ))
        ) : (
          <EmptyState title="No tickets found" subtitle="There are no support tickets matching your filters" />
        )}
      </Box>

      {!loadingTickets && !ticketsError && totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_event, newPage) => handlePageChange(newPage)}
          onRowsPerPageChange={(event) => {
            handleRowsPerPageChange(event.target.value)
            handlePageChange(0)
          }}
          sx={{ mt: 1 }}
        />
      )}

      {open && (
        <ChatModal
          open={open}
          ticketById={ticketById as Ticket}
          loadingTicketById={loadingTicketById}
          ticketByIdError={ticketByIdError}
          selectTicket={selectTicket}
          companyName={ticketById?.companyName || companyMap[selectTicket?.companyId] || ''}
          fetchTicketsById={() => refetchTicketById()}
          onClose={(shouldRefetch?: boolean) => {
            handleClose()
            if (shouldRefetch === true) refetchTickets()
            setSelectTicket({ companyId: '', ticketId: '' })
          }}
        />
      )}
    </Box>
  )
}

export default Support
